import React, { useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { pinContent, reorderPinnedContent, unpinContent } from '../../../api/pinnedContentApi';
import styles from './ContentDragDrop.module.scss';

type Item = { id: string | number; is_pinned?: boolean };
type Drop = { kind: 'pin' | 'unpin' | 'move' | 'group'; key?: string; after?: boolean };
type Options<T> = {
  items: T[];
  contextType: string;
  contextParentId?: string | number | null;
  disabled?: boolean;
  firstPage: boolean;
  targetType: (item: T) => 'folder' | 'videofeed';
  refresh: () => void;
  resolveItem?: (item: T, event: React.DragEvent<HTMLDivElement>) => T;
  canPin?: (item: T) => boolean;
  canMove?: (source: T, target: T) => boolean;
  move?: (source: T, target: T) => Promise<unknown>;
  canGroup?: (source: T, target: T) => boolean;
  group?: (source: T, target: T) => void;
  onStart?: (item: T) => void;
  onEnd?: () => void;
};

export const useContentDragDrop = <T extends Item,>(options: Options<T>) => {
  const source = useRef<T | null>(null);
  const locked = useRef(false);
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drop, setDrop] = useState<Drop | null>(null);
  const key = (item: T) => `${options.targetType(item)}:${item.id}`;
  const clear = () => {
    source.current = null;
    setActive(false);
    setDrop(null);
    options.onEnd?.();
  };
  const label = (target: Drop) => target.kind === 'pin'
    ? source.current?.is_pinned ? 'Reorder here' : 'Pin here'
    : target.kind === 'unpin' ? 'Unpin · return to sort order'
    : target.kind === 'move' ? 'Move into this folder' : 'Create a folder with these files';
  const locationDrop = (pinned: boolean, targetKey?: string, after = false): Drop | null => {
    const item = source.current;
    if (!item || options.canPin?.(item) === false || (!pinned && !item.is_pinned)) return null;
    return { kind: pinned ? 'pin' : 'unpin', key: targetKey, after };
  };
  const rowDrop = (item: T, event: React.DragEvent<HTMLDivElement>): Drop | null => {
    const dragged = source.current;
    if (!dragged || key(dragged) === key(item)) return null;
    const rect = event.currentTarget.getBoundingClientRect();
    const y = event.clientY - rect.top;
    const edge = Math.min(22, rect.height * 0.25);
    if (y <= edge || y >= rect.height - edge) return locationDrop(!!item.is_pinned, key(item), y > rect.height / 2);
    const nestedFile = (event.target as HTMLElement).closest<HTMLElement>('[data-videofeed-id]');
    if (nestedFile && nestedFile.dataset.videofeedId !== String(item.id)) return null;
    if (options.targetType(item) === 'folder' && options.canMove?.(dragged, item)) return { kind: 'move', key: key(item) };
    if (options.targetType(item) === 'videofeed' && options.targetType(dragged) === 'videofeed' && options.canGroup?.(dragged, item)) {
      return { kind: 'group', key: key(item) };
    }
    return null;
  };
  const over = (event: React.DragEvent<HTMLDivElement>, next: Drop | null) => {
    if (!source.current) return;
    event.stopPropagation();
    event.preventDefault();
    event.dataTransfer.dropEffect = next ? 'move' : 'none';
    setDrop(next);
  };
  const perform = async (target: Drop | null) => {
    const item = source.current;
    if (!item || !target || locked.current) { clear(); return; }
    const targetItem = options.items.find((entry) => key(entry) === target.key);
    clear();
    if (target.kind === 'group' && targetItem) { options.group?.(item, targetItem); return; }
    locked.current = true;
    setBusy(true);
    try {
      if (target.kind === 'move' && targetItem) {
        await options.move?.(item, targetItem);
        return;
      }
      const context = { contextType: options.contextType, contextParentId: options.contextParentId };
      const payload = { ...context, targetType: options.targetType(item), targetId: item.id };
      if (target.kind === 'unpin') {
        await unpinContent(payload);
        enqueueSnackbar('Content unpinned', { variant: 'success' });
      } else if (target.kind === 'pin') {
        if (!item.is_pinned) await pinContent(payload);
        if (options.firstPage) {
          const pins = options.items.filter((entry) => entry.is_pinned && key(entry) !== key(item));
          const index = pins.findIndex((entry) => key(entry) === target.key);
          pins.splice(index < 0 ? pins.length : index + (target.after ? 1 : 0), 0, item);
          await reorderPinnedContent({ ...context, items: pins.map((entry) => ({ targetType: options.targetType(entry), targetId: entry.id })) });
        }
        enqueueSnackbar(item.is_pinned ? 'Pinned content reordered' : 'Content pinned', { variant: 'success' });
      }
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Could not complete the drop. Please try again.', { variant: 'error' });
    } finally {
      locked.current = false;
      setBusy(false);
      options.refresh();
    }
  };
  const zoneDrop = (pinned: boolean, event: React.DragEvent<HTMLDivElement>) => {
    const rows = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[data-content-key]'))
      .filter((row) => !source.current || row.dataset.contentKey !== key(source.current));
    const next = rows.find((row) => event.clientY < row.getBoundingClientRect().top + row.getBoundingClientRect().height / 2);
    const target = next || rows[rows.length - 1];
    return locationDrop(pinned, target?.dataset.contentKey, !next);
  };
  const leave: React.DragEventHandler<HTMLDivElement> = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDrop(null);
  };
  return {
    active,
    busy,
    reset: clear,
    rowProps: (item: T) => ({
      'data-content-key': key(item),
      draggable: !options.disabled && !busy,
      style: {
        position: 'relative' as const,
        cursor: !options.disabled && !busy ? 'grab' : undefined,
        opacity: source.current && key(source.current) === key(item) ? 0.5 : undefined,
      },
      onDragStart: (event: React.DragEvent<HTMLDivElement>) => {
        if (options.disabled || locked.current) { event.preventDefault(); return; }
        event.dataTransfer.setDragImage(event.currentTarget, 20, 20);
        source.current = options.resolveItem?.(item, event) || item;
        event.stopPropagation();
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', key(source.current));
        options.onStart?.(source.current);
      },
      onDrag: () => {
        // Wait for the native drag to start before inserting an empty pinned zone.
        if (source.current) setActive(true);
      },
      onDragOver: (event: React.DragEvent<HTMLDivElement>) => over(event, rowDrop(item, event)),
      onDrop: (event: React.DragEvent<HTMLDivElement>) => {
        if (!source.current) return;
        event.preventDefault(); event.stopPropagation();
        void perform(rowDrop(item, event));
      },
      onDragLeave: leave,
      onDragEnd: clear,
    }),
    zoneProps: (pinned: boolean) => ({
      className: active ? `${styles.dropZone} ${pinned && drop?.kind === 'pin' ? styles.pinTarget : ''}` : undefined,
      onDragOver: (event: React.DragEvent<HTMLDivElement>) => over(event, zoneDrop(pinned, event)),
      onDragLeave: leave,
      onDrop: (event: React.DragEvent<HTMLDivElement>) => {
        if (!source.current) return;
        event.preventDefault(); event.stopPropagation();
        void perform(zoneDrop(pinned, event));
      },
    }),
    indicator: (item: T) => drop?.key === key(item) && (
      <div className={drop.kind === 'move' || drop.kind === 'group' ? styles.centerTarget : styles.dropLine}
        data-position={drop.after ? 'after' : 'before'} aria-hidden="true">
        <span>{label(drop)}</span>
      </div>
    ),
    emptyHint: (pinned: boolean, empty: boolean) => active && empty && (pinned || source.current?.is_pinned) && (
      <div className={`${styles.emptyZone} ${drop && !drop.key && drop.kind === (pinned ? 'pin' : 'unpin') ? styles.zoneActive : ''}`}>
        {pinned ? 'Drop here to pin' : 'Drop here to unpin · return to sort order'}
      </div>
    ),
  };
};
